import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Photo } from '../types/Photo';
import { getAllPhotos } from '../services/photoService';
import ProtectedImage from './ProtectedImage';
import { H3, H5, P, PSmall } from './Typography';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  overflow: hidden;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
`;

const BookContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  gap: 30px;
  align-items: flex-end;
  padding: 40px 100px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
    padding: 40px;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 40px 24px;
    gap: 32px;
  }
`;

const PhotoContainer = styled.div<{ $isTransitioning?: boolean; $width?: number; $height?: number }>`
  display: flex;
  width: ${(props) => (props.$width && props.$isTransitioning ? `${props.$width}px` : 'fit-content')};
  height: ${(props) => (props.$height && props.$isTransitioning ? `${props.$height}px` : 'auto')};
  max-width: 60vw;
  opacity: ${(props) => (props.$isTransitioning ? 0 : 1)};
  transition: opacity 0.8s ease-in-out;
  cursor: pointer;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    cursor: default;
  }
`;

const StyledProtectedImage = styled(ProtectedImage)`
  max-width: 60vw;
  max-height: calc(100vh - 80px);
  width: auto;
  height: auto;

  img {
    max-width: 60vw;
    max-height: calc(100vh - 80px);
    width: auto;
    height: auto;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: none;
    width: 100%;

    img {
      max-width: 100%;
      max-height: none;
      width: 100%;
      height: auto;
      object-fit: contain;
    }
  }
`;

const TextContainer = styled.div<{ $isTransitioning?: boolean }>`
  flex: 0 0 600px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 10px;
  opacity: ${(props) => (props.$isTransitioning ? 0 : 1)};
  transition: opacity 0.8s ease-in-out;

  @media (max-width: 1024px) {
    flex: 1;
    width: 100%;
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    padding-bottom: 0;
  }
`;

const Title = styled(H3)`
  margin: 0;
  line-height: 1.2;
`;

const DescriptionContainer = styled.div`
  column-count: 2;
  column-gap: 32px;
  text-align: left;

  @media (max-width: 768px) {
    column-count: 1;
  }
`;

const Description = styled(PSmall)`
  line-height: 1.8;
  margin: 0 0 8px 0;
  break-inside: avoid;
  font-size:12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PhotographerSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  break-inside: avoid;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666;
`;

const PhotographerName = styled.span`
  color: #1a1a1a;
  font-weight: 600;
  
`;

const Separator = styled.span`
  color: #666;
`;

const InstagramLink = styled.a`
  color: #666;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: #1a1a1a;
  }
`;

const MetaInfo = styled(PSmall)`
  color: #999;
  font-size: 13px;
  margin-top: 16px;
`;

const NavigationContainer = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${(props) => (props.disabled ? '#e0e0e0' : '#333')};
  background: white;
  color: ${(props) => (props.disabled ? '#ccc' : '#333')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #333;
    color: white;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }
`;

const GalleryButton = styled(NavButton)`
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const PageIndicator = styled.div`
  font-size: 11px;
  color: #666;
  font-weight: 500;
  text-align: center;
  padding: 3px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  font-size: 18px;
  color: #d32f2f;
`;

const MobileScrollContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;
    padding: 40px 24px;
    box-sizing: border-box;
    gap: 120px;
  }
`;

const MobilePhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const MobilePhotoContainer = styled.div`
  width: 100%;
`;

const MobileStyledProtectedImage = styled(ProtectedImage)`
  width: 100%;
  height: auto;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

const MobileTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const DesktopOnly = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LandingPage: React.FC = () => {
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [totalPhotos, setTotalPhotos] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showNavigation, setShowNavigation] = useState<boolean>(true);
  const [isHoveringNav, setIsHoveringNav] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [containerDimensions, setContainerDimensions] = useState<{ width: number; height: number } | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const navigate = useNavigate();
  const hideNavTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const photoContainerRef = React.useRef<HTMLDivElement>(null);

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchFirstPhoto = async () => {
      try {
        setLoading(true);
        const fetchedPhotos = await getAllPhotos();
        if (fetchedPhotos.length > 0) {
          setPhoto(fetchedPhotos[0]);
          setPhotos(fetchedPhotos);
          setTotalPhotos(fetchedPhotos.length);
        }
        setError(null);
      } catch (err) {
        setError('Failed to load photo. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFirstPhoto();
  }, []);

  // Preload image utility
  const preloadImage = React.useCallback((imageUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = imageUrl;
    });
  }, []);

  // Preload next image for smooth transition
  useEffect(() => {
    if (photos.length > 1 && !isTransitioning) {
      const nextPhoto = photos[1];
      if (nextPhoto) {
        preloadImage(nextPhoto.imageURL).catch(() => {
          // Ignore preload errors
        });
      }
    }
  }, [photos, isTransitioning, preloadImage]);

  // Mouse movement handler for navigation visibility
  useEffect(() => {
    const handleMouseMove = () => {
      setShowNavigation(true);

      if (hideNavTimeout.current) {
        clearTimeout(hideNavTimeout.current);
      }

      hideNavTimeout.current = setTimeout(() => {
        if (!isHoveringNav) {
          setShowNavigation(false);
        }
      }, 2000);
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideNavTimeout.current) {
        clearTimeout(hideNavTimeout.current);
      }
    };
  }, [isHoveringNav]);

  const handlePrevious = useCallback(() => {
    // On landing page, previous is disabled (we're at photo 0)
  }, []);

  const handleNext = useCallback(() => {
    if (isTransitioning) return;

    // Capture current container dimensions
    if (photoContainerRef.current) {
      const { width, height } = photoContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }

    setIsTransitioning(true);

    // Preload the next image
    const nextPhoto = photos[1];
    if (nextPhoto) {
      const preloadPromise = preloadImage(nextPhoto.imageURL);

      // Wait for fade out to complete (800ms)
      setTimeout(() => {
        // Navigate immediately
        navigate('/book/1');

        // Wait for image to be loaded before fading in
        preloadPromise.then(() => {
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 50);
        }).catch(() => {
          // Even if preload fails, fade in after a short delay
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 100);
        });
      }, 800);
    }
  }, [navigate, photos, isTransitioning, preloadImage]);

  const handleGallery = useCallback(() => {
    navigate('/book/1');
  }, [navigate]);

  const handlePhotoClick = useCallback(() => {
    // Disable photo click navigation on mobile
    if (window.innerWidth <= 768) return;

    if (isTransitioning) return;

    // Capture current container dimensions
    if (photoContainerRef.current) {
      const { width, height } = photoContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }

    setIsTransitioning(true);

    // Preload the next image
    const nextPhoto = photos[1];
    if (nextPhoto) {
      const preloadPromise = preloadImage(nextPhoto.imageURL);

      // Wait for fade out to complete (800ms)
      setTimeout(() => {
        // Navigate immediately
        navigate('/book/1');

        // Wait for image to be loaded before fading in
        preloadPromise.then(() => {
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 50);
        }).catch(() => {
          // Even if preload fails, fade in after a short delay
          setTimeout(() => {
            setIsTransitioning(false);
            setContainerDimensions(null);
          }, 100);
        });
      }, 800);
    }
  }, [navigate, photos, isTransitioning, preloadImage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Disable keyboard navigation on mobile
      if (window.innerWidth <= 768) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleNext]);

  if (loading) {
    return <LoadingContainer>Loading...</LoadingContainer>;
  }

  if (error || !photo) {
    return <ErrorContainer>{error || 'No photos available'}</ErrorContainer>;
  }

  const formatMetaInfo = (date: Date | string, location: string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const monthYear = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
    return `${monthYear} | ${location}`;
  };

  // Mobile view: show all photos in vertical scroll
  if (isMobile) {
    return (
      <PageContainer>
        <MobileScrollContainer>
          {photos.map((currentPhoto, index) => (
            <MobilePhotoSection key={currentPhoto.id}>
              <MobilePhotoContainer>
                <MobileStyledProtectedImage
                  src={currentPhoto.imageURL}
                  alt={currentPhoto.title}
                  loading={index === 0 ? "eager" : "lazy"}
                  showWatermark={true}
                  watermarkText={`© ${currentPhoto.title}`}
                  crop={currentPhoto.crop}
                />
              </MobilePhotoContainer>
              <MobileTextContainer>
                <Title>{currentPhoto.title}</Title>
                {currentPhoto.description && (
                  <DescriptionContainer>
                    {currentPhoto.description.split('\n').filter(para => para.trim()).map((para, idx) => (
                      <Description key={idx}>{para}</Description>
                    ))}
                    {index === 0 && (
                      <PhotographerSection>
                        <PhotographerName>Piyum Fernando</PhotographerName>
                        <Separator>|</Separator>
                        <InstagramLink
                          href="https://instagram.com/piyumfernando"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          @piyumfernando
                        </InstagramLink>
                      </PhotographerSection>
                    )}
                  </DescriptionContainer>
                )}
                {index > 0 && currentPhoto.date && currentPhoto.location && (
                  <MetaInfo>{formatMetaInfo(currentPhoto.date, currentPhoto.location)}</MetaInfo>
                )}
              </MobileTextContainer>
            </MobilePhotoSection>
          ))}
        </MobileScrollContainer>
      </PageContainer>
    );
  }

  // Desktop view: single photo with navigation
  return (
    <PageContainer>
      <DesktopOnly>
        <BookContainer>
          <PhotoContainer
            ref={photoContainerRef}
            $isTransitioning={isTransitioning}
            $width={containerDimensions?.width}
            $height={containerDimensions?.height}
            onClick={handlePhotoClick}
          >
            <StyledProtectedImage
              src={photo.imageURL}
              alt={photo.title}
              loading="eager"
              showWatermark={true}
              watermarkText={`© ${photo.title}`}
              crop={photo.crop}
            />
          </PhotoContainer>

          <TextContainer $isTransitioning={isTransitioning}>
            <Title>{photo.title}</Title>

            {photo.description && (
              <DescriptionContainer>
                {photo.description.split('\n').filter(para => para.trim()).map((para, index) => (
                  <Description key={index}>{para}</Description>
                ))}

                <PhotographerSection>
                  <PhotographerName>Piyum Fernando</PhotographerName>
                  <Separator>|</Separator>
                  <InstagramLink
                    href="https://instagram.com/piyumfernando"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    @piyumfernando
                  </InstagramLink>
                </PhotographerSection>
              </DescriptionContainer>
            )}
          </TextContainer>

          <NavigationContainer
            $visible={true}
            onMouseEnter={() => setIsHoveringNav(true)}
            onMouseLeave={() => setIsHoveringNav(false)}
          >
            <NavButton
              onClick={handlePrevious}
              disabled={true}
              aria-label="Previous photo"
            >
              ↑
            </NavButton>
            <PageIndicator>
              1 / {totalPhotos}
            </PageIndicator>
            <NavButton
              onClick={handleNext}
              aria-label="Next photo"
            >
              ↓
            </NavButton>
            <GalleryButton
              onClick={handleGallery}
              aria-label="Show all photos"
              style={{ marginTop: '12px' }}
            >
              ⊞
            </GalleryButton>
          </NavigationContainer>
        </BookContainer>
      </DesktopOnly>
    </PageContainer>
  );
};

export default LandingPage;
